import React, { useMemo, useCallback, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { Box, HStack, VStack, Text, IconButton, Select, Alert, AlertIcon, AlertDescription, Spinner, Center } from "@chakra-ui/react";
import { Icon, MedicalCodeChip } from "@codametrix/ui-components";
import classnames from 'classnames';
import {
    useHandleRubricAnswerChange,
    getOptionsFromTree,
    getValueAtPath,
    AssesmentSelect,
    RoleSelect,
    modifierAssessmentOptionsTreePath,
    modifierRoleOptionsTreePath,
} from "@codametrix/ui-common";
import { WorkflowWizard } from '../../code-bench-panel/workflow-wizard';
import { codeTypeToEntityType, getQuestionIdsToClear } from '../rubric-utils';
import { useRubricAutofill } from '../useRubricAutofill';
import {
    useWorkflowEvaluation,
    EvaluateWorkflowFn,
} from '../useWorkflowEvaluation';
import '../styles/index.scss';
export interface HighlightDrawerCodeContainerProps {
    code: CQA.ExtractedCode;
    onDelete?: (code: CQA.ExtractedCode) => void;
    onAssessmentChange?: (code: CQA.ExtractedCode, field: CMxCommonApp.SelectListField, fields: CMxCommonApp.CodeLayerField[], value: string) => void | Promise<void>;
    onRoleChange?: (code: CQA.ExtractedCode, field: CMxCommonApp.SelectListField, fields: CMxCommonApp.CodeLayerField[], value: string) => void | Promise<void>;
    riskCategories?: Accelerate.RiskCategoryTree;
    cqaCodeAssessmentRiskDictionaries?: Accelerate.CQACodeAssessmentRiskDictionaries;
    projectConfiguration?: CMxAPI.CQAProjectConfiguration;
    cptFields?: CMxCommonApp.CodeLayerField[];
    icdFields?: CMxCommonApp.CodeLayerField[];
    modifierFields?: CMxCommonApp.CodeLayerField[];
    rubricWorkflow?: CQA.CodeWorkflow;
    hasRubricVersion?: boolean;
    onAnswerChange?: (attribute: CQA.UpdateAttributeParams) => void;
    onDeleteAnswer?: (params: CQA.DeleteAttributeParams) => Promise<void>;
    onClearDownstream?: (params: CQA.ClearDownstreamAttributesParams) => void;
    evaluateFn?: EvaluateWorkflowFn;
    workItemId?: number;
    /** All span annotations in the case — required for SPAN question type */
    spans?: Accelerate.SpanAnnotation[];
    editable?: boolean;
}

const HighlightDrawerCodeContainer: React.FC<HighlightDrawerCodeContainerProps> = ({
    code,
    onDelete,
    onAssessmentChange,
    onRoleChange,
    riskCategories,
    cqaCodeAssessmentRiskDictionaries,
    projectConfiguration,
    cptFields,
    icdFields,
    modifierFields,
    rubricWorkflow,
    hasRubricVersion,
    onAnswerChange,
    onDeleteAnswer,
    onClearDownstream,
    evaluateFn,
    workItemId,
    spans,
    editable = true
}) => {
    // Gate code questions behind case-level completion
    const caseLevelComplete = useSelector(
        (state: any) => state.gradeBenchPanel?.caseLevelComplete
    );
    const codeQuestionsDisabled = caseLevelComplete === false;

    const allFields = useMemo(() => {
        if (code.codeType === 'procedure') return cptFields || [];
        if (code.codeType === 'diagnosis') return icdFields || [];
        if (code.codeType === 'modifier') return modifierFields || [];
        return [];
    }, [code.codeType, cptFields, icdFields, modifierFields]);

    const assessmentField = useMemo(() => {
        const fieldFromArray = allFields.find(f => f.key === 'assessment') as CMxCommonApp.SelectListField;
        const field = fieldFromArray || (code.codeType === 'modifier'
            ? { ...AssesmentSelect, optionsTreePath: modifierAssessmentOptionsTreePath }
            : AssesmentSelect);

        const codeValue = { ...code, original: code.original ?? false, codeAssessment: code.codeAssessment || null };
        const options = getOptionsFromTree(codeValue, field.optionsTree, field.optionsTreePath) || [];
        const currentValue = getValueAtPath(code, field.path) || '';
        const rerenderKey = field.rerenderFields?.map(f => getValueAtPath(code, [f])).join('-') || '';

        return { field, options, currentValue, rerenderKey, valueInOptions: options.includes(currentValue) };
    }, [allFields, code]);

    const roleField = useMemo(() => {
        const fieldFromArray = allFields.find(f => f.key === 'role') as CMxCommonApp.SelectListField;
        const field = fieldFromArray || (code.codeType === 'modifier'
            ? { ...RoleSelect, optionsTreePath: modifierRoleOptionsTreePath }
            : RoleSelect);

        const codeValue = { ...code, original: code.original ?? false, codeAssessment: code.codeAssessment || null };
        const options = getOptionsFromTree(codeValue, field.optionsTree, field.optionsTreePath) || [];
        const currentValue = getValueAtPath(code, field.path) || '';
        const rerenderKey = field.rerenderFields?.map(f => getValueAtPath(code, [f])).join('-') || '';

        return { field, options, currentValue, rerenderKey, valueInOptions: options.includes(currentValue) };
    }, [allFields, code]);

    const handleDelete = () => {
        if (onDelete) {
            onDelete(code);
        }
    };

    const caseAttributes = useSelector(
        (state: any) => state.gradeBenchPanel?.caseAttributes
    ) as CQA.Attribute[] | undefined;

    const rubricVersionId = projectConfiguration?.rubricVersionId;

    const codeContext = useMemo(() => ({
        original: code.original ?? null,
        code: code.value,
        sequenceNumber: code.sequenceNumber ?? 0,
        codeType: code.codeType,
    }), [code.original, code.value, code.sequenceNumber, code.codeType]);

    const {
        evaluation,
        visibleQuestions,
        filteredAnswersMap,
        autofilledAnswersMap,
        isLoading,
        error,
    } = useWorkflowEvaluation({
        workflowId: rubricWorkflow?.id,
        attributes: code.attributes || [],
        workflowQuestions: rubricWorkflow?.questions,
        codeContext,
        workItemId,
        evaluateFn: evaluateFn!,
        enabled: !!evaluateFn && !!rubricWorkflow,
        rubricVersionId,
        caseAttributes: caseAttributes || undefined,
    });

    const handleAnswerChangeWithCleanup = useHandleRubricAnswerChange(
        onAnswerChange || (() => {}),
        onDeleteAnswer || (async () => {}),
        onClearDownstream
    );

    // Use a ref to break the circular dependency between hook and handler
    const handleRubricAnswerChangeRef = useRef<(questionId: number, value: number | number[] | string) => void>(() => {});

    // Use the autofill hook to handle autofill logic (only for multiple-choice; FREE_TEXT has no autofill)
    const { trackAnswerChange, clearDownstreamTracking } = useRubricAutofill({
        entityId: code.codeId.toString(),
        entityKey: `${code.codeId}`,
        attributes: code.attributes || [],
        autofilledAnswers: autofilledAnswersMap,
        onAutofill: (questionId, answerValueId) => {
            handleRubricAnswerChangeRef.current(questionId, answerValueId);
        }
    });

    const [lastChangedCodeQuestionId, setLastChangedCodeQuestionId] = useState<number | null>(null);

    // Handle rubric answer change - wraps the shared hook with code-specific params
    // value: number = answerValueId (MULTIPLE_CHOICE/SYSTEM), number[] = spanAnnotationIds (SPAN/SPANS), string = answerDisplayText (FREE_TEXT)
    const handleRubricAnswerChange = useCallback(async (questionId: number, value: number | number[] | string) => {
        if (codeQuestionsDisabled) return;
        if (!onAnswerChange) return;

        setLastChangedCodeQuestionId(questionId);

        const downstreamIds = getQuestionIdsToClear(
            visibleQuestions,
            code.attributes || [],
            questionId
        );

        const isFreeText = typeof value === 'string';
        if (!isFreeText && typeof value === 'number') {
            trackAnswerChange(questionId, value);
        }
        clearDownstreamTracking(downstreamIds);

        // Use real attributes for the save operation (not virtual system question answers)
        // Determine which answer field to use based on question type
        const question = visibleQuestions.find(wq => wq.question.id === questionId)?.question;
        const isSpan = question?.questionType === 'SPAN';
        const isSpans = question?.questionType === 'SPANS';

        let answerParams: Record<string, unknown>;
        if (isFreeText) {
            answerParams = { answerDisplayText: value };
        } else if (isSpan || isSpans) {
            answerParams = { spanAnnotationIds: value };
        } else {
            answerParams = { answerValueId: value };
        }
        await handleAnswerChangeWithCleanup({
            entityId: code.codeId.toString(),
            entityType: codeTypeToEntityType(code.codeType),
            attributes: code.attributes || [],
            questionId,
            ...answerParams,
            downstreamQuestionIds: downstreamIds
        });
    }, [codeQuestionsDisabled, handleAnswerChangeWithCleanup, onAnswerChange, code.codeId, code.codeType, code.attributes, visibleQuestions, rubricWorkflow, trackAnswerChange, clearDownstreamTracking]);

    handleRubricAnswerChangeRef.current = handleRubricAnswerChange;

    const hasRubricWorkflow = rubricWorkflow && rubricWorkflow.questions && rubricWorkflow.questions.length > 0;
    const shouldHideAssessmentRole = Boolean(hasRubricVersion);

    const handleAssessmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (onAssessmentChange && assessmentField) {
            onAssessmentChange(code, assessmentField.field, allFields, e.target.value);
        }
    };

    const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (onRoleChange && roleField) {
            onRoleChange(code, roleField.field, allFields, e.target.value);
        }
    };

    const showAssessment = projectConfiguration?.assessment !== false;
    const showRole = projectConfiguration?.role !== false;
    const showLegacyAssessment = !shouldHideAssessmentRole && showAssessment;
    const showLegacyRole = !shouldHideAssessmentRole && showRole;

    const classNames = classnames('code-container', code.codeType);

    return (
        <Box className={classNames}>
            <VStack spacing={3} align="stretch">
                <HStack spacing={3} align="center" width="100%">
                    <MedicalCodeChip
                        code={code.value}
                        codeType={code.codeType as CMxCommonApp.ChargeSessionCodeType}
                        description={code.description}
                        editable={false}
                    />
                    <Text flex={1}>{code.description}</Text>
                    {onDelete && (
                        <IconButton
                            aria-label="Delete code"
                            icon={<Icon svgIconName="BiomeDelete" svgIconSize="16" />}
                            size="sm"
                            variant="ghost"
                            onClick={handleDelete}
                        />
                    )}
                </HStack>

                {hasRubricWorkflow && error ? (
                    <Alert status="error" variant="left-accent" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                            Unable to load questions. Please try refreshing the page.
                        </AlertDescription>
                    </Alert>
                ) : hasRubricWorkflow && isLoading && !evaluation ? (
                    <Center py={4}>
                        <Spinner size="sm" />
                    </Center>
                ) : hasRubricWorkflow && visibleQuestions.length > 0 ? (
                    <VStack spacing={3} align="stretch">
                        {codeQuestionsDisabled && (
                            <Alert status="info" variant="left-accent" borderRadius="md" mb={2} sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'center !important' }}>
                                <AlertIcon mr={2} flexShrink={0} />
                                <AlertDescription fontSize="sm">
                                    Please complete all case-level questions before answering code questions.
                                </AlertDescription>
                            </Alert>
                        )}
                        <WorkflowWizard
                            visibleQuestions={visibleQuestions}
                            attributes={code.attributes || []}
                            onAnswerChange={handleRubricAnswerChange}
                            isDisabled={codeQuestionsDisabled || !editable}
                            isLoading={isLoading}
                            autofilledAnswersMap={autofilledAnswersMap}
                            filteredAnswersMap={filteredAnswersMap}
                            spans={spans || []}
                            lastChangedQuestionId={lastChangedCodeQuestionId}
                        />
                    </VStack>
                ) : !hasRubricWorkflow ? (
                    <>
                        {showLegacyAssessment && assessmentField && (
                            <Select
                                key={assessmentField.rerenderKey}
                                placeholder={assessmentField.field.placeholder}
                                value={assessmentField.currentValue}
                                onChange={handleAssessmentChange}
                                size="sm"
                            >
                                {!assessmentField.valueInOptions && assessmentField.currentValue && (
                                    <option value={assessmentField.currentValue} disabled>
                                        {assessmentField.currentValue}
                                    </option>
                                )}
                                {assessmentField.options.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        )}

                        {showLegacyRole && roleField && (
                            <Select
                                key={roleField.rerenderKey}
                                placeholder={roleField.field.placeholder}
                                value={roleField.currentValue}
                                onChange={handleRoleChange}
                                size="sm"
                            >
                                {!roleField.valueInOptions && roleField.currentValue && (
                                    <option value={roleField.currentValue} disabled>
                                        {roleField.currentValue}
                                    </option>
                                )}
                                {roleField.options.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        )}
                    </>
                ) : null}
            </VStack>
        </Box>
    );
};

export default HighlightDrawerCodeContainer;
